import EventEmitter from "eventemitter3";
import { EventTypes } from "../core/event-bus";
import { Modals, Pages } from "../types/enums";
import { Page } from "../components/pages/page";
import { Modal } from "../components/modals/modal";

type ModalMap = Record<Modals, Modal | (() => Modal)>;

export class Router {
    private readonly _eventBus: EventEmitter;
    private readonly _routes: Partial<Record<Pages, Page>>;
    private readonly _modals: Partial<ModalMap>;
    private readonly _disableUrlNavigation: boolean;
    private readonly _onUnknownRoute?: (page: Pages) => boolean;
    private static _route: Pages;
    private static _modal: Modals | null = null;

    constructor(eventBus: EventEmitter, routes: Partial<Record<Pages, Page>>, modals: Partial<ModalMap>, options?: { disableUrlNavigation?: boolean, onUnknownRoute?: (page: Pages) => boolean }) {
        this._eventBus = eventBus;
        this._routes = routes;
        this._modals = modals;
        this._disableUrlNavigation = options?.disableUrlNavigation ?? false;
        this._onUnknownRoute = options?.onUnknownRoute;
        Router._route = Pages.LANDING;
    }

    public init() {
        this.forPages(page => page.init());
        this.forModals(popup => popup.init());
        this._eventListeners();
    }

    public static get path() {
        return Router._route;
    }

    /** How many in-app pages the current history entry is from the first one (0 = nothing to go back to). */
    public static get historyDepth(): number {
        const idx = window.history.state?.idx;
        return typeof idx === "number" ? idx : 0;
    }

    /**
     * Hardware back in the mobile app: dismisses the topmost dialog/modal, else goes back a page.
     * @returns false when there is nothing to go back to, so the app may close.
     */
    public static handleBack(): boolean {
        const openDialogs = document.querySelectorAll<HTMLDialogElement>("dialog[open]");
        const topDialog = openDialogs[openDialogs.length - 1];
        if (topDialog) {
            Router._dismissDialog(topDialog);
            return true;
        }

        if (Router.historyDepth > 0) {
            window.history.back();
            return true;
        }

        return false;
    }

    /** Back on a dialog behaves like Escape, so must-acknowledge modals (disableEscapeClose) stay open. */
    private static _dismissDialog(dialog: HTMLDialogElement): void {
        const escape = new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true });
        dialog.dispatchEvent(escape);
        if (escape.defaultPrevented)
            return; // a Modal handled Escape

        // Plain <dialog>s (Dialog message boxes) rely on the browser's native Escape: cancel, then close.
        if (dialog.dispatchEvent(new Event("cancel", { cancelable: true })))
            dialog.close();
    }

    public readonly forPages = (fn: (_: Page) => void): void => {
        Object.keys(Pages).forEach(page => {
            const route = this._routes[Pages[page as keyof typeof Pages]];
            if (route) fn(route);
        });
    }

    public readonly forModals = (fn: (_: Modal) => void): void => {
        Object.keys(Modals).forEach(key => {
            const modalEnumValue = Modals[key as keyof typeof Modals];
            const modalInstance = this._resolveModal(modalEnumValue);
            if (modalInstance)
                fn(modalInstance);
        });
    }

    private readonly _eventListeners = (): void => {
        this._eventBus.on(EventTypes.LOCATION_CHANGE, (page: Pages, options: any) => {
            this._eventBus.emit(EventTypes.MODAL_CLOSE, Router._modal);

            // Unknown routes fall back to Landing unless the host handles them (returns true).
            if (!this._routes[page] && this._onUnknownRoute?.(page))
                return;

            if (options?.back)
                this._update(page);
            else
                this._navigate(page);                                                                                                                        
        });

        this._eventBus.on(EventTypes.MODAL_CLOSED, () => { Router._modal = null; });

        window.addEventListener("popstate", () => {
            if (this._disableUrlNavigation) return;
            this._eventBus.emit(EventTypes.MODAL_CLOSE, Router._modal);
            // { back: true } makes the listener above show the page without pushing a new entry.
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, window.location.pathname as Pages, { back: true })
        });

        this._eventBus.on(EventTypes.MODAL_OPEN, this._show)

        this._eventBus.on(EventTypes.MODAL_CLOSE, (modal: Modals) => {
            this._resolveModal(modal)?.close();
        })
    }

    private _resolveModal(modal: Modals | null): Modal | null {
        if (!modal)
            return null;

        const ref = this._modals[modal];
        return typeof ref === "function" ? ref() : ref ?? null;
    }

    private readonly _update = (page: Pages): void => {
        this._routes[Router.path]?.hide();
        Router._route = this._routes[page] ? page : Pages.LANDING;
        this._routes[Router.path]?.show();

        // Reset page back up to top
        const mainElement = document.querySelector("body");
        if (mainElement)
            mainElement.scrollTop = 0;
    }

    private readonly _navigate = (page: Pages): void => {
        const skipPage = [Pages.CURBSIDE, Pages.UNSUBSCRIBE];
        if (!this._disableUrlNavigation && !skipPage.includes(page)) {
            let urlStr = '';
            if (window.location.search && [Pages.LANDING, Pages.MENU].includes(page)) {
                const url = new URL(page, window.location.origin);
                url.search = window.location.search;
                // The app's one-time SSO token expires in a minute; a reload or back to it would sign the user out.
                url.searchParams.delete("token");
                url.hash = window.location.hash;
                urlStr = url.toString();
            } else {
                urlStr = window.location.origin + page;
            }

            // Re-navigating to the page already showing (startup routing, tapping the current nav item)
            // replaces the entry; a duplicate would make back appear to do nothing.
            if (Router._isCurrentPage(urlStr))
                window.history.replaceState({ idx: Router.historyDepth }, "", urlStr);
            else
                window.history.pushState({ idx: Router.historyDepth + 1 }, "", urlStr);
        }
        this._update(page)
    }

    private static _isCurrentPage(urlStr: string): boolean {
        const target = new URL(urlStr);
        const current = new URL(window.location.href);
        current.searchParams.delete("token");
        return target.pathname === current.pathname && target.search === current.search;
    }

    private readonly _show = (modal: Modals): void => {
        const current = this._resolveModal(Router._modal);
        current?.close();
        Router._modal = modal;

        const instance = this._resolveModal(Router._modal);
        instance?.open();

        // Reset modal back up to top
        const modalElement = document.getElementById(instance?.id ?? "");
        if (modalElement)
            modalElement.scrollTop = 0;
    }
}